/**
 * Resolution-workflow API layer (milestone 03-13). Follows the settled envelope
 * convention: `apiFetch` returns the raw `{ data }` envelope; this layer unwraps
 * `data` and maps snake_case↔camelCase. The workflow adds one small record +
 * orchestration endpoints; worksheet/offer/form endpoints are reused elsewhere.
 */

import { getSubmission, listClientFormSubmissions, listTaxForms } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { apiFetch } from "@/lib/api";
import type {
  ResolutionWorkflow,
  WorkflowDetermination,
  WorkflowStep,
  SelectedForm,
} from "@/features/workflows/types";

interface Envelope<T> {
  data: T;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

function rename<T>(value: unknown, fn: (k: string) => string, depth = 0): T {
  if (Array.isArray(value)) return value.map((v) => rename(v, fn, depth)) as T;
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      // Don't rewrite the opaque `determination` cache's inner keys — it's the
      // backend's already-camelCase-at-read snapshot; renaming twice is a no-op
      // but the warnings array of strings must pass through untouched.
      out[fn(k)] = rename(v, fn, depth + 1);
    }
    return out as T;
  }
  return value as T;
}

const camelize = <T>(v: unknown): T => rename<T>(v, toCamel);
const decamelize = (v: unknown): unknown => rename(v, toSnake);

const base = (caseId: string) => `/cases/${caseId}/workflows/`;

// --- workflow record ---------------------------------------------------------

/** POST — start or resume the IA workflow (idempotent per case+type). */
export async function startOrResumeWorkflow(
  caseId: string,
  type = "IA",
): Promise<ResolutionWorkflow> {
  const res = await apiFetch<Envelope<unknown>>(base(caseId), {
    method: "POST",
    json: { type },
  });
  return camelize<ResolutionWorkflow>(res.data);
}

export async function listWorkflows(caseId: string): Promise<ResolutionWorkflow[]> {
  const res = await apiFetch<Envelope<unknown[]>>(base(caseId));
  return res.data.map((r) => camelize<ResolutionWorkflow>(r));
}

export async function getWorkflow(
  caseId: string,
  id: string,
): Promise<ResolutionWorkflow> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}${id}/`);
  return camelize<ResolutionWorkflow>(res.data);
}

interface WorkflowPatch {
  currentStep?: WorkflowStep;
  selectedForm?: SelectedForm;
}

export async function patchWorkflow(
  caseId: string,
  id: string,
  patch: WorkflowPatch,
): Promise<ResolutionWorkflow> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}${id}/`, {
    method: "PATCH",
    json: decamelize(patch),
  });
  return camelize<ResolutionWorkflow>(res.data);
}

/** GET — assemble the IA determination (proxies the Tier 2.2 offer service). */
export async function getDetermination(
  caseId: string,
  id: string,
): Promise<WorkflowDetermination> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}${id}/determination/`);
  return camelize<WorkflowDetermination>(res.data);
}

/** POST — mark the workflow completed + write a Case system-note. */
export async function completeWorkflow(
  caseId: string,
  id: string,
): Promise<ResolutionWorkflow> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}${id}/complete/`, {
    method: "POST",
  });
  return camelize<ResolutionWorkflow>(res.data);
}

/** PATCH the case status to Pending IRS (the optional post-complete prompt).
 * The case write serializer is partial, so only `status` is sent. */
export async function markCasePendingIrs(caseId: string): Promise<void> {
  await apiFetch<Envelope<unknown>>(`/cases/${caseId}/`, {
    method: "PATCH",
    json: { status: "PENDING_IRS" },
  });
}

// --- output-form submissions (step 7/8, reuse the Tier 3 forms layer) --------

let _taxFormCache: Map<string, string> | null = null;

/** Resolve a form number (e.g. "9465") to its `reference.TaxForm` id. */
export async function resolveTaxFormId(formNumber: string): Promise<string> {
  if (!_taxFormCache) {
    const forms = await listTaxForms();
    _taxFormCache = new Map(forms.map((f) => [f.formNumber, f.id]));
  }
  const id = _taxFormCache.get(formNumber);
  if (!id) throw new Error(`Form ${formNumber} is not registered.`);
  return id;
}

interface ApiSubmissionLite {
  id: string;
}

// In-flight get-or-create promises, keyed by client+case+form. `FormSubmission`
// has no DB unique constraint on (client, tax_form, case), so two concurrent
// callers (e.g. a step's mount-load racing a Generate click) could each miss the
// not-yet-created row and POST a duplicate. Sharing the promise per key makes
// concurrent calls resolve to the same submission.
const _inflight = new Map<string, Promise<FormSubmission>>();

async function _getOrCreate(
  clientId: string,
  caseId: string,
  formNumber: string,
): Promise<FormSubmission> {
  const taxFormId = await resolveTaxFormId(formNumber);
  const existing = await listClientFormSubmissions(clientId);
  const match = existing.find((s) => s.formNumber === formNumber && s.case === caseId);
  if (match) return match;
  const res = await apiFetch<Envelope<ApiSubmissionLite>>("/forms/", {
    method: "POST",
    json: { client: clientId, tax_form: taxFormId, case: caseId },
  });
  // Re-fetch through the forms api so we get the fully-mapped submission.
  return getSubmission(res.data.id);
}

/**
 * Get (or create, bound to the case) the client's submission for a form number.
 * The workflow's output forms are the client's own submissions, queried by
 * client + form number and attached to the case so they surface on it.
 * Concurrent calls for the same (client, case, form) share one request so no
 * duplicate submission is created.
 */
export function getOrCreateCaseSubmission(
  clientId: string,
  caseId: string,
  formNumber: string,
): Promise<FormSubmission> {
  const key = `${clientId}|${caseId}|${formNumber}`;
  const pending = _inflight.get(key);
  if (pending) return pending;
  const promise = _getOrCreate(clientId, caseId, formNumber).finally(() =>
    _inflight.delete(key),
  );
  _inflight.set(key, promise);
  return promise;
}
